import * as React from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Link2Icon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface LinkEditorProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultUrl?: string
  defaultText?: string
  defaultIsNewTab?: boolean
  onSave: (url: string, text?: string, isNewTab?: boolean) => void
}

/** Link form shared by the toolbar popover and the bubble menu — laid out like the app's popover forms (Label + Input stack, small primary button). */
export const LinkEditBlock = React.forwardRef<HTMLDivElement, LinkEditorProps>(
  ({ onSave, defaultIsNewTab, defaultUrl, defaultText, className }, ref) => {
    const formRef = React.useRef<HTMLDivElement>(null)
    const [url, setUrl] = React.useState(defaultUrl || "")
    const [text, setText] = React.useState(defaultText || "")
    const [isNewTab, setIsNewTab] = React.useState(defaultIsNewTab || false)

    const handleSave = React.useCallback(
      (e: React.SyntheticEvent) => {
        e.preventDefault()
        if (formRef.current) {
          const inputs = Array.from(formRef.current.querySelectorAll("input"))
          if (inputs.every((input) => input.checkValidity())) {
            onSave(url, text, isNewTab)
          } else {
            inputs.forEach((input) => {
              if (!input.checkValidity()) input.reportValidity()
            })
          }
        }
      },
      [onSave, url, text, isNewTab]
    )

    const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handleSave(e)
    }

    React.useImperativeHandle(ref, () => formRef.current as HTMLDivElement)

    return (
      <div ref={formRef} className={cn("flex flex-col gap-3", className)}>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="mt-link-url">URL</Label>
          <Input
            id="mt-link-url"
            type="url"
            required
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={submitOnEnter}
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="mt-link-text">
            Text{" "}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="mt-link-text"
            type="text"
            placeholder="Link text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={submitOnEnter}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="mt-link-new-tab" className="font-normal">
            Open in new tab
          </Label>
          <Switch
            id="mt-link-new-tab"
            checked={isNewTab}
            onCheckedChange={setIsNewTab}
          />
        </div>

        <div className="flex justify-end pt-1">
          <Button type="button" size="sm" onClick={handleSave}>
            <Link2Icon />
            Save link
          </Button>
        </div>
      </div>
    )
  }
)

LinkEditBlock.displayName = "LinkEditBlock"

export default LinkEditBlock
